import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import client from "@/lib/api";
import { toast } from "sonner";
import { Scale, Search, Plus, FileOutput, Loader2, Gavel, CheckCircle2, XCircle, Clock, AlarmClock } from "lucide-react";

const STATUS_META = {
  pending: ["PENDING", "#F59E0B", Clock],
  attempted: ["ATTEMPTED", "#22D3EE", Clock],
  served: ["SERVED", "#4ade80", CheckCircle2],
  returned_unserved: ["RETURNED UNSERVED", "#EF4444", XCircle],
  cancelled: ["CANCELLED", "#6B7280", XCircle],
};
const inp = "w-full bg-[#141824] border border-[#232838] px-2.5 py-2 text-sm font-mono text-white outline-none focus:border-[#3B82F6]";
const lbl = "font-mono text-[10px] text-[#8299bd] uppercase";

const EMPTY = { doc_type: "warrant", case_number: "", court: "District Court of Maryland", county: "",
  plaintiff: "", defendant: "", servee_name: "", servee_address: "", servee_description: "",
  hearing_date: "", linked_cc: "", notes: "" };

export default function ServiceOfProcess() {
  const [params] = useSearchParams();
  const [meta, setMeta] = useState({ doc_types: {}, manners: [] });
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [attempt, setAttempt] = useState({ outcome: "served", manner: "", location: "", notes: "", attempted_at: "" });
  const [busy, setBusy] = useState(false);
  const [alerts, setAlerts] = useState([]);
  useEffect(() => { client.get("/service/alerts").then((r) => setAlerts(r.data.alerts)).catch(() => {}); }, [rows]);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/service", { params: { q, status } });
      setRows(data);
      setSel((s) => (s ? data.find((d) => d.id === s.id) || null : s));
    } catch { /* role-gated */ }
  }, [q, status]);
  useEffect(() => { client.get("/service/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  // Deep links from a Call for Service: ?cc=…&new=1 opens a pre-linked create form; ?sel=… opens a record.
  useEffect(() => {
    const cc = params.get("cc");
    if (params.get("new") === "1") { setForm((f) => ({ ...f, linked_cc: cc || f.linked_cc })); setCreating(true); }
    const selId = params.get("sel");
    if (selId) client.get("/service").then((r) => { const m = r.data.find((d) => d.id === selId); if (m) setSel(m); }).catch(() => {});
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!form.servee_name.trim() || !form.case_number.trim()) { toast.error("Case number and person to serve are required."); return; }
    setBusy(true);
    try {
      const { data } = await client.post("/service", form);
      toast.success(`${data.service_number} opened`);
      setForm(EMPTY); setCreating(false); load(); setSel(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create record"); }
    setBusy(false);
  };

  const logAttempt = async () => {
    if (attempt.outcome === "served" && !attempt.manner) { toast.error("Select the manner of service."); return; }
    setBusy(true);
    try {
      await client.post(`/service/${sel.id}/attempts`, attempt);
      toast.success(attempt.outcome === "served" ? "Marked SERVED — affidavit ready" : "Attempt logged");
      setAttempt({ outcome: "served", manner: "", location: "", notes: "", attempted_at: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not log attempt"); }
    setBusy(false);
  };

  const downloadAffidavit = async () => {
    try {
      const res = await client.get(`/service/${sel.id}/affidavit.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${sel.service_number}-affidavit.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Affidavit of Service (MD) downloaded");
    } catch { toast.error("Could not generate affidavit"); }
  };

  const downloadAttemptLog = async () => {
    try {
      const res = await client.get(`/service/${sel.id}/attempts-report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${sel.service_number}-attempt-log.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Service Attempt Log (Due Diligence) downloaded");
    } catch { toast.error("Could not generate attempt log"); }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6" data-testid="service-page">
        <PageHeader icon={Scale} title="SERVICE OF PROCESS" subtitle="Warrant · subpoena · court document service with MD Affidavit of Service" />
        {alerts.length > 0 && (
          <div data-testid="due-diligence-banner" className="mt-4 border border-[#F59E0B]/60 bg-[#F59E0B]/10 rounded px-3 py-2.5">
            <div className="flex items-center gap-2 font-head font-bold text-sm text-[#F59E0B]">
              <AlarmClock size={16} /> DUE DILIGENCE — {alerts.length} unserved with hearing{alerts.length > 1 ? "s" : ""} approaching
            </div>
            <div className="mt-1.5 space-y-1">
              {alerts.map((a) => (
                <button key={a.id} data-testid={`due-alert-${a.service_number}`} onClick={() => { setSel(rows.find((r) => r.id === a.id) || a); setCreating(false); }}
                  className="w-full text-left flex items-center gap-2 font-mono text-[11px] hover:bg-[#F59E0B]/10 px-1.5 py-1 rounded">
                  <span className={`font-bold ${a.days_left < 0 ? "text-[#EF4444]" : a.days_left <= 2 ? "text-[#F59E0B]" : "text-[#E0B23C]"}`}>
                    {a.days_left < 0 ? `OVERDUE ${-a.days_left}d` : a.days_left === 0 ? "HEARING TODAY" : `${a.days_left}d LEFT`}
                  </span>
                  <span className="text-[#8299bd]">{a.service_number}</span>
                  <span className="text-white">{a.servee_name}</span>
                  <span className="text-[#5f7396] ml-auto">hearing {a.hearing_date} · {(a.attempts || []).length} attempt{(a.attempts || []).length === 1 ? "" : "s"}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <div className="flex items-center gap-2 bg-[#141824] border border-[#232838] px-3 py-2 w-72 focus-within:border-[#3B82F6]">
            <Search size={13} className="text-[#6B7280]" />
            <input data-testid="service-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Case #, name, court…"
              className="flex-1 bg-transparent outline-none text-sm font-mono text-white placeholder:text-[#6B7280]" />
          </div>
          <select data-testid="service-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#141824] border border-[#232838] px-2 py-2 text-xs font-mono text-white outline-none">
            <option value="">All statuses</option>
            {Object.entries(STATUS_META).map(([k, [label]]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <button data-testid="service-new-btn" onClick={() => { setCreating(true); setSel(null); }}
            className="ml-auto flex items-center gap-1.5 px-3 py-2 bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-head font-bold text-xs uppercase"><Plus size={14} /> New Service</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {/* list */}
          <div className="border border-[#232838] rounded-lg divide-y divide-[#1a2130] max-h-[70vh] overflow-y-auto" data-testid="service-list">
            {rows.map((r) => {
              const [label, color, Icon] = STATUS_META[r.status] || STATUS_META.pending;
              return (
                <button key={r.id} data-testid={`service-row-${r.service_number}`} onClick={() => { setSel(r); setCreating(false); }}
                  className={`w-full text-left px-3 py-2.5 hover:bg-[#10141f] ${sel?.id === r.id ? "bg-[#10141f]" : ""}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-[#8299bd]">{r.service_number}</span>
                    <span className="font-mono text-[9px] uppercase border border-[#232838] px-1 py-px rounded-sm text-[#E0B23C]">{meta.doc_types[r.doc_type] || r.doc_type}</span>
                    <span className="ml-auto font-mono text-[10px] font-bold flex items-center gap-1" style={{ color }}><Icon size={11} /> {label}</span>
                  </div>
                  <div className="text-sm text-white mt-0.5">{r.servee_name} <span className="font-mono text-[11px] text-[#5f7396]">· Case {r.case_number}</span></div>
                  <div className="font-mono text-[10px] text-[#5f7396]">{r.court}{r.county ? ` · ${r.county}` : ""}</div>
                </button>
              );
            })}
            {rows.length === 0 && <div className="p-4 font-mono text-sm text-[#8299bd]">No service records{q ? ` match “${q}”` : " yet"}.</div>}
          </div>

          {/* detail / create */}
          <div className="border border-[#232838] rounded-lg p-4 max-h-[70vh] overflow-y-auto">
            {creating ? (
              <div className="space-y-3" data-testid="service-create-form">
                <div className="font-head font-bold text-sm">NEW SERVICE OF PROCESS</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>Document type</label>
                    <select data-testid="service-doc-type" value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className={inp}>
                      {Object.entries(meta.doc_types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select></div>
                  <div><label className={lbl}>Case number *</label><input data-testid="service-case-number" className={inp} value={form.case_number} onChange={(e) => setForm({ ...form, case_number: e.target.value })} /></div>
                  <div><label className={lbl}>Court</label><input data-testid="service-court" className={inp} value={form.court} onChange={(e) => setForm({ ...form, court: e.target.value })} /></div>
                  <div><label className={lbl}>County (MD)</label><input data-testid="service-county" className={inp} value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} placeholder="e.g. Baltimore County" /></div>
                  <div><label className={lbl}>Plaintiff / Issuing party</label><input data-testid="service-plaintiff" className={inp} value={form.plaintiff} onChange={(e) => setForm({ ...form, plaintiff: e.target.value })} /></div>
                  <div><label className={lbl}>Defendant</label><input data-testid="service-defendant" className={inp} value={form.defendant} onChange={(e) => setForm({ ...form, defendant: e.target.value })} /></div>
                  <div><label className={lbl}>Person to serve *</label><input data-testid="service-servee" className={inp} value={form.servee_name} onChange={(e) => setForm({ ...form, servee_name: e.target.value })} /></div>
                  <div><label className={lbl}>Service address</label><input data-testid="service-address" className={inp} value={form.servee_address} onChange={(e) => setForm({ ...form, servee_address: e.target.value })} /></div>
                  <div><label className={lbl}>Hearing date</label><input data-testid="service-hearing-date" className={inp} value={form.hearing_date} onChange={(e) => setForm({ ...form, hearing_date: e.target.value })} placeholder="YYYY-MM-DD" /></div>
                  <div><label className={lbl}>Linked CC #</label><input data-testid="service-linked-cc" className={inp} value={form.linked_cc} onChange={(e) => setForm({ ...form, linked_cc: e.target.value })} placeholder="2026-xxxxx" /></div>
                </div>
                <div><label className={lbl}>Notes</label><textarea data-testid="service-notes" rows={2} className={inp} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <button data-testid="service-create-submit" disabled={busy} onClick={create}
                  className="w-full py-2.5 bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-head font-bold text-sm flex items-center justify-center gap-2">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Gavel size={15} />} OPEN SERVICE RECORD
                </button>
              </div>
            ) : sel ? (
              <div className="space-y-3" data-testid="service-detail">
                <div className="flex items-center gap-2">
                  <div className="font-head font-bold">{sel.service_number}</div>
                  <span className="font-mono text-[10px] uppercase border border-[#232838] px-1.5 py-0.5 rounded-sm text-[#E0B23C]">{meta.doc_types[sel.doc_type] || sel.doc_type}</span>
                  <span className="ml-auto font-mono text-xs font-bold" style={{ color: (STATUS_META[sel.status] || [])[1] }}>{(STATUS_META[sel.status] || [])[0]}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-[11px]">
                  <div><span className="text-[#5f7396]">CASE</span> <span className="text-white">{sel.case_number}</span></div>
                  <div><span className="text-[#5f7396]">COURT</span> <span className="text-white">{sel.court}{sel.county ? ` · ${sel.county}` : ""}</span></div>
                  <div><span className="text-[#5f7396]">SERVE</span> <span className="text-white">{sel.servee_name}</span></div>
                  <div><span className="text-[#5f7396]">ADDRESS</span> <span className="text-white">{sel.servee_address || "—"}</span></div>
                  {sel.plaintiff && <div><span className="text-[#5f7396]">PLAINTIFF</span> <span className="text-white">{sel.plaintiff}</span></div>}
                  {sel.defendant && <div><span className="text-[#5f7396]">DEFENDANT</span> <span className="text-white">{sel.defendant}</span></div>}
                  {sel.hearing_date && <div><span className="text-[#5f7396]">HEARING</span> <span className="text-white">{sel.hearing_date}</span></div>}
                  {sel.linked_cc && <div><span className="text-[#5f7396]">CC</span> <span className="text-[#22D3EE]">{sel.linked_cc}</span></div>}
                  <div className="col-span-2"><span className="text-[#5f7396]">OPENED BY</span> <span className="text-white">{sel.created_by_title || sel.created_by}</span></div>
                </div>

                <div className="border-t border-[#232838] pt-3">
                  <div className="font-mono text-[10px] uppercase text-[#8299bd] mb-1.5">Service attempts ({(sel.attempts || []).length})</div>
                  {(sel.attempts || []).map((a, i) => (
                    <div key={i} className="font-mono text-[11px] text-[#E5E7EB] py-1 border-b border-[#1a2130]" data-testid={`service-attempt-${i}`}>
                      <span className="text-[#5f7396]">#{a.seq || i + 1}</span>{" "}
                      <b style={{ color: a.outcome === "served" ? "#4ade80" : "#F59E0B" }}>{a.outcome.toUpperCase()}</b> · {new Date(a.attempted_at || a.ts).toLocaleString("en-GB", { hour12: false })} · {a.by}
                      {a.location && <div className="text-[#8299bd]">{a.location}</div>}
                      {a.manner && <div className="text-[#8299bd]">{a.manner}</div>}
                      {a.notes && <div className="text-[#5f7396]">{a.notes}</div>}
                    </div>
                  ))}
                  {sel.status !== "served" && sel.status !== "cancelled" && (
                    <div className="mt-2.5 space-y-2 bg-[#10141f] border border-[#232838] rounded p-2.5">
                      <div className="grid grid-cols-2 gap-2">
                        <select data-testid="attempt-outcome" value={attempt.outcome} onChange={(e) => setAttempt({ ...attempt, outcome: e.target.value })} className={inp}>
                          <option value="served">SERVED</option><option value="attempted">ATTEMPTED — no contact</option>
                          <option value="refused">REFUSED</option><option value="unable">UNABLE TO SERVE</option>
                        </select>
                        <input data-testid="attempt-location" className={inp} placeholder="Location" value={attempt.location} onChange={(e) => setAttempt({ ...attempt, location: e.target.value })} />
                      </div>
                      {attempt.outcome === "served" && (
                        <select data-testid="attempt-manner" value={attempt.manner} onChange={(e) => setAttempt({ ...attempt, manner: e.target.value })} className={inp}>
                          <option value="">— Manner of service (MD) —</option>
                          {meta.manners.map((mn) => <option key={mn} value={mn}>{mn}</option>)}
                        </select>
                      )}
                      <div>
                        <label className={lbl}>Date / time of attempt (blank = now)</label>
                        <input data-testid="attempt-datetime" type="datetime-local" className={inp}
                          value={attempt.attempted_at} onChange={(e) => setAttempt({ ...attempt, attempted_at: e.target.value })} />
                      </div>
                      <input data-testid="attempt-notes" className={inp} placeholder="Notes" value={attempt.notes} onChange={(e) => setAttempt({ ...attempt, notes: e.target.value })} />
                      <button data-testid="attempt-submit" disabled={busy} onClick={logAttempt}
                        className="w-full py-2 bg-[#4ade80]/15 border border-[#4ade80]/50 text-[#4ade80] font-head font-bold text-xs uppercase hover:bg-[#4ade80]/25">
                        {busy ? "Saving…" : attempt.outcome === "served" ? "Mark Served" : "Log Attempt"}
                      </button>
                    </div>
                  )}
                </div>

                <button data-testid="affidavit-download-btn" onClick={downloadAffidavit}
                  className="w-full py-2.5 bg-[#E0B23C]/15 border border-[#E0B23C]/50 text-[#E0B23C] font-head font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#E0B23C]/25">
                  <FileOutput size={15} /> AFFIDAVIT OF SERVICE (MD) — PDF
                </button>
                <button data-testid="attempt-log-download-btn" onClick={downloadAttemptLog}
                  className="w-full py-2.5 bg-[#22D3EE]/10 border border-[#22D3EE]/50 text-[#22D3EE] font-head font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#22D3EE]/20">
                  <FileOutput size={15} /> SERVICE ATTEMPT LOG (DUE DILIGENCE) — PDF
                </button>
                <div className="font-mono text-[9px] text-[#5B6472]">Affirmation per Md. Rule 1-304. The Attempt Log documents every retry (date, time, location, notes) — court-ready even when the document remains unserved.</div>
              </div>
            ) : (
              <div className="font-mono text-sm text-[#8299bd] flex items-center justify-center h-40">Select a record or open a new service.</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
